import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Sparkles, ImageOff, Loader2, LogIn } from 'lucide-react';
import { log } from '../../lib/logger';
import { renderCanvasMockup } from '../../lib/canvasMockupCompositor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable fade / zoom-in presets for AnimatePresence states below.
// Hoisted to module scope so Framer-Motion sees the same object reference
// on every render (code review item: inline motion objects cause
// unnecessary re-subscribes).
const FADE_INIT = { opacity: 0 };
const FADE_IN = { opacity: 1 };
const FADE_OUT = { opacity: 0 };
const IMG_ENTER_INIT = { opacity: 0, scale: 1.05 };
const IMG_ENTER_ANIMATE = { opacity: 1, scale: 1 };
const IMG_ENTER_TRANSITION = { duration: 0.35, ease: 'easeOut' };

// Sparkle burst fade-out preset — same reasoning as FADE_* above.
const SPARKLE_INIT = { opacity: 0.9 };
const SPARKLE_ANIM = { opacity: 0 };
const SPARKLE_TRANS = { duration: 1.4 };

// Knight breathe-pulse preset — gentle scale loop only. Filter-based
// drop-shadow halo was removed in iter82 because the new MP4 source has
// a baked-in white background; we use `mix-blend-mode: multiply` on the
// video so the white pixels mathematically vanish against the dark
// green render-progress panel. A `drop-shadow` filter on the wrapper
// would emit a soft rectangle around the bounding box (since the
// underlying bitmap is fully opaque before the blend), which doesn't
// follow the figure silhouette and looks like a stray halo. Scale
// pulses UPWARD ONLY (1.0 → 1.035 → 1.0) so the knight is never
// smaller than full-size.
const KNIGHT_ANIMATE = {
  scale: [1, 1.035, 1],
};
const KNIGHT_TRANSITION = {
  scale: { duration: 2.2, repeat: Infinity, ease: 'easeInOut' },
};
// Separate one-shot fade-in handled by the parent motion.div via FADE_*
// presets — keeps the steady-state scale loop tidy and prevents the
// 0.92 entry keyframe from re-firing every 4.4s like it did in iter66.


/**
 * Live AI-rendered product preview shown on Quote Calculator Page 2.
 *
 * Generates a photorealistic stack-of-product image (e.g., 500 × 4" holo
 * stickers on a desk) via Gemini Nano Banana. Debounced + aggressively
 * cached:
 *   1. Bucket inputs (size → 0.5", qty → tier) so minor tweaks reuse the
 *      same image.
 *   2. Check localStorage for a cached URL for this bucket key → instant.
 *   3. Otherwise POST /api/product-preview/generate → backend returns a
 *      key + public GET url; we stash the url in localStorage.
 */
export const LiveProductPreview = ({
  productType,
  material,
  formatName,
  width,
  height,
  unit,
  quantity,
  artworkId,
  // Local File/Blob from the customer's drag-drop. Preferred over
  // artworkId for the canvas compositor because it's already in
  // memory — no network round-trip required.
  artworkFile = null,
  // Page 2 cutline shape — rectangle / rounded-rectangle / circle / custom.
  // Applied as a canvas clip path for pixel-perfect cut-line preview.
  cutlineShape,
  // White-ink option ('keep' | 'clear'). When 'clear' is picked on a
  // transparency-eligible material (holographic / glitter / clear /
  // foil / DTF), the canvas compositor strips near-white pixels to
  // alpha=0 so the material backing shows through — critical for
  // PDF uploads where pdfjs paints an opaque white page background.
  whiteInkOption = 'keep',
  // Backend (category|format|finish) triple. Kept on the prop list for
  // future per-category scene variations, but the v1 canvas compositor
  // uses a single "sticker pack on emerald velvet" layout for all
  // categories — quality is consistent and we can layer scene
  // variations later without touching call-sites.
  // eslint-disable-next-line no-unused-vars
  scene,
  // Auth gating — anonymous visitors get a friendly CTA prompting them
  // to drop their email so we can save the rendered preview to a draft.
  isAuthenticated = true,
  onRequireAuth,
  // Hero variant — when true, renders a much larger, more visually
  // emphasized preview for placement at the TOP of the page (above the
  // artwork/size/qty sections). Smaller variant is kept for legacy usage.
  hero = false,
  // Optional slot rendered right under the "Your Live Mockup" heading
  // (hero variant only). Used by Page 2 to tuck the "Upload your artwork"
  // panel inside the same card so it feels like one cohesive step.
  heroSlot = null,
  // Optional slot rendered ABOVE the heading (hero variant only).
  // Used by Page 2 for the micro-step progress meter.
  progressSlot = null,
  // iter114p — optional render slot that REPLACES the default "Drop
  // your artwork below…" idle UI. Page 2 routes the merged-into-
  // mockup dropzone (`MockupIdleDropzone`) here so the empty mockup
  // window IS the dropzone — single panel, single interaction.
  idleSlot = null,
}) => {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error | auth-required
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  // iter114s.29 — high-quality custom dieline contour fetched from the
  // backend (same OpenCV pipeline as the PDF proof). Falls back to the
  // in-browser radial-scan tracer when null. Keyed on artworkId so a
  // new staging upload triggers a fresh fetch.
  const [backendContour, setBackendContour] = useState(null);
  const lastKeyRef = useRef(null);
  // Track the artworkId/file pair last rendered so we don't re-render
  // when the customer tweaks size/qty/material/cutline (sticky policy
  // preserved from iter114i — for canvas the cost is lower but the
  // image-swap UX is still jarring).
  const prevArtworkIdRef = useRef(artworkId || null);
  const prevArtworkFileRef = useRef(artworkFile || null);
  // Ref for the knight video so we can deterministically kick autoplay
  // on browsers that block autoplay until the element is in the DOM AND
  // ready (notably iOS Safari). Same pattern as PlaceOrderLoadingOverlay.
  const knightVideoRef = useRef(null);

  // ------------------------------------------------------------------
  // Subtle parallax: as the customer scrolls the QuoteCalculator page,
  // the knight figure drifts ever-so-slightly LEFT (-32 px max). This
  // signals "this is a living, premium product" without ever competing
  // with the loading copy or the actual mockup content. Bound to the
  // PAGE scrollY (not the panel) because the panel itself doesn't
  // scroll internally; mapped through `[0, 800]` so the full drift
  // happens within the typical viewport-height of scroll, then clamps
  // (Framer's default behaviour) so further scrolling doesn't keep
  // pulling the knight off-screen. `useScroll` subscribes via a single
  // passive scroll listener at the document level, so cost is O(1)
  // regardless of how many panels are mounted.
  // ------------------------------------------------------------------
  const { scrollY } = useScroll();
  const knightParallaxX = useTransform(scrollY, [0, 800], [0, -32], {
    clamp: true,
  });
  // Opposing-direction parallax on the right-side status block — drifts
  // RIGHT a subtle +12 px over the same 800 px window. Combined with the
  // knight's leftward drift, this creates a small layered-depth effect:
  // the foreground (knight) and the background (status copy) move in
  // opposite directions as the eye scrolls past, signalling that the
  // panel is a scene rather than a flat card. Tiny magnitude (12 px vs
  // 32 px) keeps it firmly in "subtle motion" territory — never distracts
  // from the loading message itself.
  const statusParallaxX = useTransform(scrollY, [0, 800], [0, 12], {
    clamp: true,
  });

  // Decide whether the inputs are sufficient to render a meaningful
  // preview. Canvas compositor (iter114k) requires the customer's
  // actual artwork — either as a local File/Blob (preferred, instant)
  // or as a staging-artwork id (we fetch the bytes from the backend).
  const canGenerate = useMemo(() => {
    if (!productType || !material) return false;
    return !!artworkFile || !!artworkId;
  }, [productType, material, artworkFile, artworkId]);

  // Deterministic autoplay kick for the knight loading video — some
  // browsers (notably iOS Safari) block autoplay until the element is
  // both in the DOM AND `canplay`-ready. Same pattern as
  // PlaceOrderLoadingOverlay's video kick.
  useEffect(() => {
    if (status !== 'loading') return undefined;
    const v = knightVideoRef.current;
    if (!v) return undefined;
    const tryPlay = () => v.play?.().catch(() => {});
    tryPlay();
    v.addEventListener('canplay', tryPlay, { once: true });
    return () => v.removeEventListener('canplay', tryPlay);
  }, [status]);

  // iter114s.29 — fetch the smooth dieline contour from the backend
  // whenever the staging artwork or cutline shape changes. Same
  // OpenCV-backed extractor the PDF proof uses, so the live mockup's
  // die-line ends up pixel-identical to the production proof. The
  // result is cached server-side per (artwork_id, mtime), so a re-
  // fetch within a session is O(1).
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !artworkId || cutlineShape !== 'custom') {
      setBackendContour(null);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/files/artwork/staging/${artworkId}/contour`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (r.data?.applicable && Array.isArray(r.data.points) && r.data.points.length >= 3) {
          setBackendContour(r.data.points);
        } else {
          // Backend couldn't extract — fall back to the JS radial scan.
          setBackendContour(null);
        }
      } catch (err) {
        if (cancelled) return;
        log.warn('[LiveProductPreview] backend contour fetch failed:', err?.response?.status || err?.message);
        setBackendContour(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, artworkId, cutlineShape]);

  // ------------------------------------------------------------------
  // CANVAS MOCKUP RENDER (iter114k)
  // ------------------------------------------------------------------
  // Replaces the Gemini Nano Banana Universal-Key-billed render with a
  // 100%-free, instant browser-side canvas composite. The customer's
  // actual artwork is composited onto an EG3-branded "sticker pack on
  // emerald velvet" surface with the chosen cut shape applied
  // pixel-perfect. See `lib/canvasMockupCompositor.js` for the paint
  // pipeline.
  //
  // Sticky-mockup policy preserved from iter114i: once a mockup
  // renders, tweaking size/qty/material/format DOESN'T re-paint —
  // only a new artwork upload (artworkFile or artworkId change) does.
  // Even though canvas re-renders are free, the image-swap is still
  // visually disruptive.
  useEffect(() => {
    if (!canGenerate) {
      setStatus('idle');
      setUrl(null);
      lastKeyRef.current = null;
      return undefined;
    }

    // Anonymous visitors can't fetch a backend-stored artwork (the
    // staging-preview endpoint is owner-scoped), and we don't want to
    // attach a preview to a non-existent account. Show the auth CTA.
    // Local-file path (artworkFile present) DOES work for guests
    // because the file is in browser memory.
    if (!isAuthenticated && !artworkFile) {
      setStatus('auth-required');
      setUrl(null);
      return undefined;
    }

    // Sticky guard: only re-paint if something visible to the eye
    // actually changed. With iter114l we pin on artwork + cut shape +
    // material (different materials get visually different sticker
    // backings — holographic vs glitter vs foil — so a material swap
    // SHOULD re-render). iter114s — also re-render when the customer
    // toggles White Ink so they see the live alpha-key effect on
    // transparency-eligible substrates. Size/qty/format are still
    // no-ops once a render exists.
    const sameArtwork =
      (artworkId || null) === prevArtworkIdRef.current &&
      (artworkFile || null) === prevArtworkFileRef.current;
    const haveExistingRender = !!url && status === 'ready';
    const cutShapeKey = cutlineShape || 'rectangle';
    const materialKey = material || '';
    const whiteInkKey = whiteInkOption || 'keep';
    // iter114s.29 — add a stable signature for the backend contour so
    // a contour arriving AFTER the initial render triggers a re-paint
    // (otherwise the customer keeps staring at the jagged radial-scan
    // mockup until they touch a control).
    const contourSig = backendContour ? `bc${backendContour.length}` : 'no-bc';
    const renderKey = `${cutShapeKey}|${materialKey}|${whiteInkKey}|${artworkId || 'file'}|${contourSig}`;
    const visualsChanged = haveExistingRender && lastKeyRef.current !== null && lastKeyRef.current !== renderKey;
    if (haveExistingRender && sameArtwork && !visualsChanged) return undefined;

    let cancelled = false;
    setStatus('loading');
    setError(null);

    (async () => {
      try {
        // Resolve a Blob source: prefer the in-memory File from the
        // dropzone; fall back to fetching the staging artwork bytes
        // by id so draft-resume scenarios still render.
        let blob = artworkFile;
        if (!blob && artworkId) {
          const resp = await axios.get(
            `${API_URL}/api/files/artwork/staging/${artworkId}/preview`,
            { responseType: 'blob', withCredentials: true },
          );
          blob = resp.data;
        }
        if (cancelled) return;
        // Scene seed — stable per artwork so cut-shape/material tweaks
        // don't shuffle the background out from under the customer.
        // A new artwork upload (different File ref or different
        // staging id) DOES land on a different scene, which keeps the
        // experience visually fresh across uploads.
        const sceneSeed = artworkId || artworkFile?.name || 'fallback';
        const dataUrl = await renderCanvasMockup({
          artworkBlob: blob,
          cutShape: cutShapeKey,
          material: materialKey,
          sceneSeed,
          whiteInkOption: whiteInkKey,
          customContourOverride: backendContour,
        });
        if (cancelled) return;
        prevArtworkIdRef.current = artworkId || null;
        prevArtworkFileRef.current = artworkFile || null;
        lastKeyRef.current = renderKey;
        setUrl(dataUrl);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        log.warn('[LiveProductPreview] canvas mockup render failed:', err?.message || err);
        setError(err?.message || 'Preview is taking a bit longer than usual.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canGenerate, artworkFile, artworkId, cutlineShape, material, whiteInkOption, isAuthenticated, url, status, backendContour]);

  return (
    <div
      className={
        hero
          ? 'rounded-2xl bg-gradient-to-br from-[#F4FBF6] via-[#E8F1E6] to-[#F4FBF6] shadow-xl ring-1 ring-[#6FBF73]/30 p-6 md:p-8 overflow-hidden relative'
          : 'sticky top-4 rounded-xl bg-gradient-to-br from-[#4A7C59]/5 via-white to-[#F4FBF6] border border-[#4A7C59]/20 shadow-sm p-4'
      }
      data-testid="live-product-preview"
    >
      {/* Ambient shimmer — hero only. iter114s.69: amber shimmer dropped
          its alpha after the surrounding palette flipped to pale green
          (the previous 0.20 alpha read as a yellow smudge on light BG). */}
      {hero && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-[#6FBF73]/15 via-[#A8D8AC]/10 to-transparent blur-3xl animate-pulse"
        />
      )}

      {/* Progress meter slot — hero only. Sits above the heading so the
          customer's eye catches the micro-step map before the VIP badge. */}
      {hero && progressSlot}

      {/* iter114s.30 — heading row tightened: badge moved to the
          dropzone (see MockupIdleDropzone), so the row no longer
          needs the 4-unit bottom margin that previously made room
          for the 20-pixel-tall floating badge. mb-4 → mb-2 for hero,
          mb-3 → mb-2 for the compact variant — gives the same dense
          rhythm as the rest of the wizard. */}
      <div className={hero ? 'relative flex items-center gap-3 mb-2' : 'flex items-center gap-2 mb-2'}>
        <Sparkles className={hero ? 'w-6 h-6 text-[#6FBF73] drop-shadow-[0_0_10px_rgba(111,191,115,0.5)]' : 'w-4 h-4 text-[#4A7C59]'} />
        <h3 className={hero ? 'text-2xl md:text-3xl font-bold text-[#14281D] tracking-tight' : 'text-sm font-semibold text-gray-800'}>
          {hero ? 'Your Live Mockup' : 'Live AI Preview'}
        </h3>
        {!hero && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-[#4A7C59]/70 font-medium">
            Gemini Nano Banana
          </span>
        )}
      </div>

      {/* iter114r — heroSlot was previously rendered ABOVE the mockup
          window so the customer saw artwork-form fields before the
          mockup itself. Customer asked to flip the order so the
          mockup-with-dropzone sits IMMEDIATELY under the "Your Live
          Mockup" title and the artwork-detail controls (cut shape /
          white ink / notes / file info) sit BELOW the mockup. The
          slot now renders after the quality hint at the bottom of
          this component. */}

      <div className={
        hero
          ? 'relative aspect-[16/10] md:aspect-[21/10] w-full bg-white rounded-xl overflow-hidden ring-1 ring-[#6FBF73]/20 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4)]'
          : 'relative aspect-square w-full bg-white rounded-lg overflow-hidden border border-gray-200'
      }>
        <AnimatePresence mode="wait">
          {status === 'idle' && (
            <motion.div
              key="idle"
              initial={FADE_INIT}
              animate={FADE_IN}
              exit={FADE_OUT}
              className={
                idleSlot
                  ? 'absolute inset-0'  // Slot is positioned absolutely by its own container styles (see MockupIdleDropzone).
                  : (hero
                    ? 'absolute inset-0 flex flex-col items-center justify-center text-center px-6'
                    : 'absolute inset-0 flex flex-col items-center justify-center text-center px-4')
              }
              data-testid="live-preview-idle"
            >
              {idleSlot ? (
                idleSlot
              ) : (
                <>
                  <ImageOff className={hero ? 'w-16 h-16 text-[#6FBF73]/40 mb-3' : 'w-10 h-10 text-gray-300 mb-2'} />
                  <p className={hero ? 'text-base text-[#1F3D2C]/75 max-w-sm' : 'text-xs text-gray-500'}>
                    {hero
                      ? 'Drop your artwork below to watch the mockup render in seconds.'
                      : 'Pick a size and quantity to see your stack rendered live.'}
                  </p>
                </>
              )}
            </motion.div>
          )}

          {status === 'auth-required' && (
            <motion.div
              key="auth"
              initial={FADE_INIT}
              animate={FADE_IN}
              exit={FADE_OUT}
              className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#4A7C59]/10 via-[#F4FBF6] to-white text-center px-4"
              data-testid="live-preview-auth-required"
            >
              <LogIn className="w-10 h-10 text-[#4A7C59] mb-2" />
              <p className="text-xs font-semibold text-gray-900">Sign in to preview</p>
              <p className="text-[11px] text-gray-600 mt-1 px-2">
                Drop your email — no password needed — to see your stickers
                rendered live by our AI.
              </p>
              <button
                type="button"
                onClick={() => onRequireAuth?.()}
                data-testid="live-preview-auth-cta"
                className="mt-3 text-xs font-semibold text-white bg-[#4A7C59] hover:bg-[#3D6A4A] rounded-full px-4 py-1.5 transition-colors"
              >
                Continue →
              </button>
            </motion.div>
          )}

          {status === 'loading' && (
            <motion.div
              key="loading"
              initial={FADE_INIT}
              animate={FADE_IN}
              exit={FADE_OUT}
              className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 overflow-hidden"
              data-testid="live-preview-loading"
            >
              {/* Inline SVG filter that does true white→alpha keying on
                  the knight video. The supplied MP4 has a baked-in
                  white background, and we explored several alternatives:
                  - VP9/VP8 alpha WebM: this ffmpeg/libvpx build doesn't
                    emit the alpha sidecar properly (alpha_mode tag set
                    but pixels stay opaque), so true alpha encoding
                    isn't viable here.
                  - CSS mix-blend-mode: multiply: partially works but
                    leaves a visible lighter rectangle anywhere the
                    panel-bg gradient is brighter than dark green, and
                    dims the figure's bright green glow.
                  - Canvas chroma keying: heavy CPU cost on every frame.
                  This SVG filter wins on every axis — it's GPU-
                  composited per frame, gives PIXEL-PERFECT alpha
                  cutout, preserves the figure's green glow at full
                  saturation, and works on Chrome/Firefox/Safari/Edge.

                  Pipeline (iter114g):
                    1. feColorMatrix → A_out = clamp(-R - G - B + 2.4, 0, 1)
                       → white (1,1,1) = -3+2.4 = -0.6 → 0  (transparent)
                       → mid-grey (.5)  =  -1.5+2.4 = 0.9 (opaque)
                       → black (0,0,0)  =  +2.4    → 1   (fully opaque)
                       BUT — also makes pixels OUTSIDE the video bbox
                       (source alpha = 0) compute A_out = 2.4 → 1, i.e.
                       OPAQUE BLACK. With the iter113 filter region
                       padded to ±5% around the bbox, those padding
                       pixels rendered as two solid black vertical bars
                       flanking the figure (customer screenshot bug).
                    2. feComponentTransfer (table) → smooths the
                       alpha threshold so anti-aliased near-white edges
                       still suppress halos but with linear falloff
                       (no jaggy aliasing per iter113).
                    3. feComposite operator="in" SourceGraphic →
                       AND the computed alpha with the SOURCE alpha
                       channel. Pixels outside the video bbox have
                       source alpha = 0, so this masks the padding
                       region back to transparent and kills the black
                       bars. Inside the video frame source alpha = 1
                       so the keyed-out figure renders unchanged. */}
              <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                  <filter id="knight-white-key" x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
                    <feColorMatrix
                      type="matrix"
                      values="1 0 0 0 0
                              0 1 0 0 0
                              0 0 1 0 0
                              -1 -1 -1 0 2.4"
                      result="keyed"
                    />
                    <feComponentTransfer in="keyed" result="keyedSmoothed">
                      <feFuncA type="table" tableValues="0 0 0 1" />
                    </feComponentTransfer>
                    {/* Critical mask: AND with source alpha so the
                        padding region stays transparent rather than
                        flooding to opaque black. */}
                    <feComposite in="keyedSmoothed" in2="SourceGraphic" operator="in" />
                  </filter>
                </defs>
              </svg>
              {/* Knight + status text stacked in a single flex column.
                  iter114s.17 — customer feedback: knight got smaller in
                  iter114s.13 ("brought down" was misread as "make
                  smaller"). Restore a more dominant knight (~85% of
                  panel width / 90% height) and bring the pair slightly
                  closer to the bottom (`pb-4` → just enough room for
                  the spinner + "Rendering your mockup…" caption). */}
              <div
                className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none"
                aria-hidden="true"
              >
                <motion.div
                  animate={KNIGHT_ANIMATE}
                  transition={KNIGHT_TRANSITION}
                  style={{
                    transformOrigin: 'center center',
                    willChange: 'transform',
                    x: knightParallaxX,
                  }}
                  className="w-auto max-w-[85%] max-h-[90%] select-none flex items-end justify-center"
                  data-testid="live-preview-knight"
                >
                  <video
                    ref={knightVideoRef}
                    src="/eg3-knight-rendering.mp4"
                    poster="/eg3-knight-rendering-poster.jpg"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    style={{
                      filter: 'url(#knight-white-key) saturate(1.08)',
                    }}
                    className="h-full w-auto max-w-full object-contain object-center block"
                    data-testid="live-preview-knight-video"
                  />
                </motion.div>
                {/* Spinner + status text — directly below the knight,
                    centred. Sits inside the same flex column so the
                    pair never drifts apart on different panel sizes.
                    Preserved the parallax counter-drift via x. */}
                <motion.div
                  style={{ x: statusParallaxX, willChange: 'transform' }}
                  className="flex flex-col items-center text-center mt-2 mb-3"
                  data-testid="live-preview-status"
                >
                  <Loader2 className="w-6 h-6 text-[#4A7C59]/80 animate-spin mb-1" />
                  <p className="text-[10px] text-[#4A7C59]/70 mt-0.5 font-medium">
                    Rendering your mockup…
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}

          {status === 'ready' && url && (
            <motion.img
              key={url}
              src={url}
              alt="AI-rendered product preview"
              initial={IMG_ENTER_INIT}
              animate={IMG_ENTER_ANIMATE}
              exit={FADE_OUT}
              transition={IMG_ENTER_TRANSITION}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="live-preview-image"
            />
          )}

          {status === 'error' && (
            <motion.div
              key="error"
              initial={FADE_INIT}
              animate={FADE_IN}
              exit={FADE_OUT}
              className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 text-center px-6 py-4"
              data-testid="live-preview-error"
            >
              {/* On-brand error treatment — uses the EG3 knight thumb +
                  amber tones so the customer reads "the herald is taking
                  a moment" rather than "the app is broken". The most
                  common error in the canvas pipeline (iter114k) is an
                  artwork file we can't decode (PSD / AI / large CMYK
                  PDF), so the copy steers towards switching format. */}
              <img
                src="/eg3-knight-thumb.png"
                alt=""
                aria-hidden="true"
                className="w-14 h-14 mb-2 opacity-90 grayscale-[20%]"
              />
              <p className="text-xs font-semibold text-amber-800 max-w-xs leading-relaxed">
                {error || 'Preview is taking a bit longer than usual.'}
              </p>
              <p className="text-[11px] text-gray-600 mt-1.5 max-w-xs leading-relaxed">
                Your quote is unaffected — feel free to continue. PNG, JPG,
                or WebP files work best for the live mockup.
              </p>
              <button
                type="button"
                onClick={() => {
                  lastKeyRef.current = null;
                  prevArtworkIdRef.current = null;
                  prevArtworkFileRef.current = null;
                  setError(null);
                  setStatus('idle');
                }}
                data-testid="live-preview-retry"
                className="mt-3 text-[11px] font-semibold text-white bg-[#4A7C59] hover:bg-[#3D6A4A] rounded-full px-3 py-1 transition-colors"
              >
                Try again
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sparkle burst — only shown briefly when a new image settles in */}
        {status === 'ready' && url && (
          <motion.div
            key={`sparkle-${url}`}
            initial={SPARKLE_INIT}
            animate={SPARKLE_ANIM}
            transition={SPARKLE_TRANS}
            className="pointer-events-none absolute inset-0"
          >
            <SparkleBurst />
          </motion.div>
        )}
      </div>

      {status === 'ready' && hero && (
        <div
          className="mt-3 px-3 py-2 rounded-lg bg-white/70 border border-[#6FBF73]/30 text-[11px] text-[#1F3D2C]/80 space-y-1"
          data-testid="live-preview-quality-hint"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#4A7C59] flex-shrink-0" />
            <span>
              Live mockup of <span className="text-[#2F5D3A] font-semibold">your actual artwork</span> with the chosen cut shape. Final printed colours may shift slightly — your proof PDF locks them in.
            </span>
          </div>
          {/* iter114s.13 — customer-requested refresh hint. The Canvas
              compositor occasionally lands on a cache-hit edge case
              where toggling another cut shape re-triggers the render
              cleanly. Surfacing this self-help one-liner saves a
              support email when it happens. */}
          <div
            className="flex items-center gap-2 pl-[22px] italic text-[#1F3D2C]/65"
            data-testid="live-preview-refresh-hint"
          >
            <span>
              If the live render seems unresponsive, cycling through another shape option can refresh it right away.
              {' '}A quick double-click on the cut shape selection may also help kickstart the rendering.
            </span>
          </div>
        </div>
      )}

      {/* iter114r — heroSlot now renders BELOW the mockup window so
          the customer's eye lands on the live preview first, then the
          artwork-detail controls (Cut Shape / White Ink / Notes /
          file info) sit underneath as the "what about my artwork?"
          follow-up section. */}
      {hero && heroSlot && (
        <div className="mt-4 -mx-2 md:-mx-4">
          {heroSlot}
        </div>
      )}
    </div>
  );
};

// Stable sparkle positions + motion props hoisted to module scope so
// framer-motion doesn't re-initialize on every render.
const SPARKLE_POSITIONS = [
  { id: 'tl', top: '8%', left: '12%', delay: 0 },
  { id: 'tr', top: '18%', right: '10%', delay: 0.1 },
  { id: 'ml', top: '46%', left: '6%', delay: 0.2 },
  { id: 'br', bottom: '14%', right: '14%', delay: 0.05 },
  { id: 'bc', bottom: '8%', left: '42%', delay: 0.15 },
  { id: 'mc', top: '28%', left: '52%', delay: 0.25 },
];
const SPARKLE_INITIAL = { opacity: 0, scale: 0 };
const SPARKLE_ANIMATE = { opacity: [0, 1, 0], scale: [0, 1.3, 0] };

// Six small sparkles fading out around the image — cheap, pure CSS-ish vibe.
const SparkleBurst = () => (
  <>
    {SPARKLE_POSITIONS.map((p) => (
      <motion.div
        key={p.id}
        initial={SPARKLE_INITIAL}
        animate={SPARKLE_ANIMATE}
        transition={{ duration: 1.2, delay: p.delay }}
        style={p}
        className="absolute w-3 h-3"
      >
        <Sparkles className="w-full h-full text-amber-300 drop-shadow-[0_0_4px_rgba(252,211,77,0.8)]" />
      </motion.div>
    ))}
  </>
);

export default LiveProductPreview;
